/**
 * [PGF Draft Board Refresh v1.1 §A] DraftBoardChatTicker - bottom-pinned
 * ticker for the Standalone Draft Board.
 *
 * Behavior (§A.3-A.5):
 *   • FIFO queue of user chat + system-event fillers.
 *   • Chat: only messages arriving AFTER mount are queued (no backfill,
 *     §A.3.1). Same-manager messages within 500ms coalesce with a
 *     middle-dot separator (verdict 6.B).
 *   • Fillers: `{team} on the clock` / `{team} nominated {last} for $X` /
 *     `{team} wins {last} for $X`. Only fired when queue is empty and
 *     ≥8s since last exit (§A.3.2). Each filler fires ONCE per event
 *     (dedup key = nominator_id / nomination_id / sale identifier).
 *   • Never Adam commentary, never bid updates during nomination,
 *     never historical/backfilled chat (§A.3.3).
 *   • Animation: constant-velocity CSS translateX from right edge to
 *     off-left-edge over ~17s (§A.4, slowed from 14s by Snake Board
 *     Fixes item 4). Multiple entries can be visible simultaneously;
 *     <DraftMarquee> releases each one only after the previous entry has
 *     travelled its own rendered width plus a fixed gap.
 *   • Owned solely by StandaloneDraftBoard - never rendered elsewhere
 *     (§A.6).
 */
import { useEffect, useRef, useState } from "react";
import { useDraft } from "../../contexts/DraftContext";
import { useTheme } from "../../contexts/ThemeContext";
import { personaNameColorFor } from "../../utils/personaColors";
import DraftMarquee from "./DraftMarquee";

// Dead-air threshold before fillers may fire. §A.3.2 = 8s.
const DEAD_AIR_MS = 8000;
// Coalesce same-manager messages arriving within this window. Verdict 6.B.
const COALESCE_WINDOW_MS = 500;

let _tickerEntryId = 0;
const makeEntryId = () => `tk_${Date.now()}_${_tickerEntryId++}`;

/**
 * Extract the manager for a chat message. Chat rows carry `sender_id`
 * (shared shape from `chat_persistence.py`, used by both auction and
 * snake) - join to draftState.managers.
 * [Stabilization fix - Feb 2026] Previously checked `user_id` /
 * `manager_id` / `author_id`, none of which exist on the actual payload,
 * so every real chat message resolved to no manager and was silently
 * dropped - auction chat never reached the ticker.
 */
function resolveManager(managers, msg) {
  const uid = msg?.sender_id;
  if (!uid || !managers) return null;
  return managers[uid] || null;
}

function lastNameOf(player) {
  const name = player?.name || player?.player_name || "";
  const parts = name.trim().split(/\s+/);
  return parts[parts.length - 1] || name || "";
}

export default function DraftBoardChatTicker({ roundBeat }) {
  const { draftState, chatMessages, nomination, soldInfo } = useDraft();
  const { isDark } = useTheme();

  const managers = draftState?.managers || {};

  // Queue of currently-visible entries.
  const [queue, setQueue] = useState([]);

  // Chat backfill guard - snapshot ids present at mount so we never
  // surface historical messages. §A.3.1.
  const seenChatIdsRef = useRef(new Set());
  const chatBaselineDoneRef = useRef(false);

  // Filler dedup keys - one filler per triggering event.
  const seenFillerKeysRef = useRef(new Set());

  // Timing bookkeeping.
  const lastExitAtRef = useRef(Date.now());
  const lastChatByManagerRef = useRef({}); // { manager_id: { entryId, ts } }

  // Establish chat baseline on first render - do NOT surface anything
  // already in the log.
  useEffect(() => {
    if (chatBaselineDoneRef.current) return;
    (chatMessages || []).forEach((m) => {
      if (m?.id) seenChatIdsRef.current.add(m.id);
    });
    chatBaselineDoneRef.current = true;
  }, [chatMessages]);

  // Push helper - release timing (width-derived stagger) is owned by
  // <DraftMarquee>, which is the only layer that can measure a rendered
  // entry's width.
  const pushEntry = (entry, coalesceKey) => {
    const now = Date.now();
    const enriched = { ...entry, id: makeEntryId() };
    setQueue((q) => [...q, enriched]);
    if (coalesceKey) {
      lastChatByManagerRef.current[coalesceKey] = { entryId: enriched.id, ts: now };
    }
    return enriched.id;
  };

  // Update helper - for coalescing existing same-manager entry.
  const appendToEntry = (entryId, moreText) => {
    setQueue((q) =>
      q.map((e) => (e.id === entryId ? { ...e, body: `${e.body} · ${moreText}` } : e)),
    );
  };

  // ── Chat ingestion ────────────────────────────────────────────────
  useEffect(() => {
    if (!chatBaselineDoneRef.current) return;
    (chatMessages || []).forEach((m) => {
      if (!m?.id || seenChatIdsRef.current.has(m.id)) return;
      seenChatIdsRef.current.add(m.id);
      // Skip system/bot-narration rows; ticker only surfaces manager chat.
      // §A.3.3. [Stabilization fix - Feb 2026] The payload's actual shape
      // (chat_persistence.py) carries `is_system`, not a
      // `chat_filter_category`/`category` field (that enum is stamped on
      // the outer WS envelope, not the inner `data` this ticker reads,
      // so the old check was always a no-op).
      if (m.is_system) return;
      const mgr = resolveManager(managers, m);
      if (!mgr) return;
      const body = m.message_body || m.body || m.text || "";
      if (!body) return;
      const uid = mgr.user_id;
      const coalescePrev = lastChatByManagerRef.current[uid];
      const now = Date.now();
      if (coalescePrev && now - coalescePrev.ts < COALESCE_WINDOW_MS) {
        // Same manager, within 500ms - append via middle dot. Verdict 6.B.
        appendToEntry(coalescePrev.entryId, body);
        lastChatByManagerRef.current[uid] = { ...coalescePrev, ts: now };
        return;
      }
      pushEntry({
        type: "chat",
        teamName: (mgr.team_name || mgr.user_name || "").toUpperCase(),
        teamColor: personaNameColorFor(mgr, isDark),
        body,
      }, uid);
    });
  }, [chatMessages, managers, isDark]);

  // ── Filler fire helper ───────────────────────────────────────────
  const maybePushFiller = (key, entry) => {
    if (seenFillerKeysRef.current.has(key)) return;
    // §A.3.2: only push if queue empty AND ≥ 8s since last exit.
    if (queue.length > 0) return;
    if (Date.now() - lastExitAtRef.current < DEAD_AIR_MS) return;
    seenFillerKeysRef.current.add(key);
    pushEntry(entry);
  };

  // ── On-the-clock filler ───────────────────────────────────────────
  const nominatorId = draftState?.current_nominator_id;
  useEffect(() => {
    if (!nominatorId) return;
    const mgr = managers[nominatorId];
    if (!mgr) return;
    maybePushFiller(`on_clock:${nominatorId}`, {
      type: "filler_clock",
      teamName: (mgr.team_name || "").toUpperCase(),
      teamColor: personaNameColorFor(mgr, isDark),
      body: "on the clock",
      isFiller: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nominatorId]);

  // ── Nomination filler ─────────────────────────────────────────────
  useEffect(() => {
    if (!nomination?.player?.id) return;
    const nomKey = `nom:${nomination.nomination_id || nomination.player.id}`;
    const bidderId = nomination.nominator_id || nomination.opening_bidder_id;
    const mgr = bidderId ? managers[bidderId] : null;
    if (!mgr) return;
    const opening = nomination.opening_bid || nomination.current_bid || 1;
    maybePushFiller(nomKey, {
      type: "filler_nom",
      teamName: (mgr.team_name || "").toUpperCase(),
      teamColor: personaNameColorFor(mgr, isDark),
      body: `nominated ${lastNameOf(nomination.player)} for $${opening}`,
      isFiller: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nomination?.nomination_id, nomination?.player?.id]);

  // ── Sale filler ───────────────────────────────────────────────────
  useEffect(() => {
    if (!soldInfo?.player?.id) return;
    const saleKey = `sale:${soldInfo.sale_id || soldInfo.request_id || soldInfo.player.id}`;
    const winnerId = soldInfo.winner_id;
    const mgr = winnerId ? managers[winnerId] : null;
    if (!mgr) return;
    const winningBid = soldInfo.winning_bid ?? soldInfo.final_bid ?? 0;
    maybePushFiller(saleKey, {
      type: "filler_sale",
      teamName: (mgr.team_name || "").toUpperCase(),
      teamColor: personaNameColorFor(mgr, isDark),
      body: `wins ${lastNameOf(soldInfo.player)} for $${winningBid}`,
      isFiller: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soldInfo?.sale_id, soldInfo?.player?.id]);

  // ── Round-beat filler - PGF Draft Board Sweep Item 1. Fed by
  // `roundBeat` (StandaloneDraftBoard's full-history-derived counter,
  // NOT the WS-trimmed completed_sales array NOMINATION_READY uses
  // internally - see the founder's added guardrail). Same dedup/dead-air
  // gate as every other filler so it never spams over live chat.
  const bodyColor = isDark ? "#F5F5F5" : "#0A0A0A";
  useEffect(() => {
    if (!roundBeat) return;
    maybePushFiller(`round_beat:${roundBeat.key}`, {
      type: "filler_round",
      teamName: "",
      teamColor: bodyColor,
      body: `ROUND ${roundBeat.round} STARTING!`,
      isFiller: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundBeat?.key]);

  // Remove an entry when its animation ends (exited off left edge).
  const handleAnimationEnd = (entryId) => {
    lastExitAtRef.current = Date.now();
    setQueue((q) => q.filter((e) => e.id !== entryId));
  };

  return (
    <DraftMarquee
      queue={queue}
      onEntryAnimationEnd={handleAnimationEnd}
      isDark={isDark}
      testId="draft-board-chat-ticker"
    />
  );
}
